import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setExams } from '@/store/slices/dashboard/examSlice';
import { setTests } from '@/store/slices/dashboard/testSlice';
import { RootState } from '@/store/index';
import { getExamsApiCall, getTestsApiCall } from '@/services/api';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import ExamCard from '@/components/dashboard/examCard';
import { WatchSectionNotFound } from '@/components/common/watchSectionNotFound';
import { GraduationCap } from 'lucide-react';

import WatchLoader from '@/pages/dashboard/loaders/watchLoader';

import './evaluate.css'
import { examType } from '@/pages/dashboard/constants';
import { addCurrentThemeClassToEle } from '@/utils';


export default function Evaluate(props: any) {
    const { dashboard } = useLocalTranslation();
    const watchEmpty = dashboard.watchSection.empty;
    const noExamRef = useRef<HTMLDivElement>(null);
    const noTestRef = useRef<HTMLDivElement>(null);

    const [examIsLoading, setExamIsLoading] = useState(true);
    const [testIsLoading, setTestIsLoading] = useState(true);

    const dispatch = useDispatch();
    const examDetails = useSelector((state: RootState) => state.examReducer.exams);
    const testDetails = useSelector((state: RootState) => state.testReducer.tests);
    const currentTheme = useSelector((state: RootState) => state.currentThemeReducer.currentTheme);


    useEffect(() => {
        const shouldLoad = Boolean(props.forceLoad) || (props.activeKey && props.activeKey === "evaluate");
        if (shouldLoad) {
            if (examDetails.length === 0) {
                getExamDetails();
            } else {
                setExamIsLoading(false);
            }

            if (testDetails.length === 0) {
                getTestDetails();
            } else {
                setTestIsLoading(false);
            }
        }
    }, [props.activeKey, props.forceLoad]);

    useEffect(() => {
        if (testDetails.length === 0 && noTestRef.current) {
            let val = window.innerHeight - noTestRef.current.getBoundingClientRect().bottom - 120
            if (val < 490) {
                val = 500
            }
            noTestRef.current.style.height = `${val}px`;
        }
    }, [testDetails])

    useEffect(() => {
        if (examDetails.length === 0 && noExamRef.current) {
            noExamRef.current.style.height = `500px`;
        }
    }, [examDetails])



    const getExamDetails = async () => {
        const examDetails = await getExamsApiCall();
        if (examDetails.data && examDetails.status) {
            setExamIsLoading(false);
            dispatch(setExams(examDetails.data));
            // console.log("examDetails : ", examDetails.data)

            //@ts-ignore
            // dispatch(setExams(ExamDummyData.data)); // For testing dummy data
            // dispatch(setExams([])); // For testing empty state
        }
    };

    const getTestDetails = async () => {
        const testDetails = await getTestsApiCall();
        if (testDetails.data && testDetails.status) {
            setTestIsLoading(false);
            dispatch(setTests(testDetails.data));
            // console.log("testDetails : ", testDetails.data)

            //@ts-ignore
            // dispatch(setDashboardDetails(WatchDummyData.data)); // For testing dummy data
            // dispatch(setDashboardDetails({})); // For testing empty state
        }
    }

    const embedInWatch = Boolean(props.embedInWatch);

    if (embedInWatch) {
        if (examIsLoading || testIsLoading) {
            return (
                <div className="evaluateViewMain evaluate_embedWatch">
                    <WatchLoader source="evalute" />
                </div>
            );
        }

        const hasExams = examDetails.length > 0;
        const hasTests = testDetails.length > 0;

        return (
            <div className="evaluateViewMain evaluate_embedWatch">
                <div className={`${addCurrentThemeClassToEle(currentTheme, 'examMain0')}`}>
                    {hasExams || hasTests ? (
                        <div className="examMain examMain_embedWatch rounded-[7px] grid max-[1200px]:grid-cols-1 grid-cols-2 max-[1200px]:bg-white">
                            {examDetails.map((exam: any, index: number) => (
                                <ExamCard
                                    key={`ev-exam-${exam._id ?? exam.examId ?? index}`}
                                    exam={exam}
                                    examType={examType.exam}
                                    source="dashboard"
                                    hideThumbnail
                                    watchEmbedCard
                                />
                            ))}
                            {testDetails.map((exam: any, index: number) => (
                                <ExamCard
                                    key={`ev-test-${exam._id ?? exam.testExamId ?? index}`}
                                    exam={exam}
                                    examType={examType.practice}
                                    source="dashboard"
                                    hideThumbnail
                                    watchEmbedCard
                                />
                            ))}
                        </div>
                    ) : (
                        <WatchSectionNotFound
                            icon={GraduationCap}
                            title={watchEmpty.evaluationTitle}
                            subtitle={
                                watchEmpty.evaluationSubtitle?.trim()
                                    ? watchEmpty.evaluationSubtitle
                                    : undefined
                            }
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className='evaluateViewMain'>
            {examIsLoading ? (
            // {true ? ( // testing
                <WatchLoader
                    source="evalute"
                />
            ) : (
                <div className={`${addCurrentThemeClassToEle(currentTheme, 'examMain0')}`}>
                    <h1 className='text-[22px] font-medium mb-[25px] examPlaceHolderTitle'>
                        {dashboard.evaluateSection.exam.title}
                    </h1>
                    {examDetails.length > 0 ? (
                        <div className="examMain rounded-[7px] grid max-[1200px]:grid-cols-1 grid-cols-2 bg-[#f8f8f8] max-[1200px]:bg-white">
                            {examDetails.map((exam: any, index: number) => (
                                <ExamCard
                                    key={index}
                                    exam={exam}
                                    examType={examType.exam}
                                    source={"dashboard"}
                                />
                            ))}
                        </div>
                    ) : (
                        <div
                            ref={noExamRef}
                            className='examMain text-[#949494] rounded-[10px] flex max-[1200px]:bg-[#ffffff] bg-[#F8F8F8] items-center justify-center'
                        >
                            <p>{dashboard.evaluateSection.exam.whenNoExam}</p>
                        </div>
                    )}
                </div>
            )}


            {testIsLoading ? (
            // {true ? ( // testing
                <WatchLoader
                    source="test"
                />
            ) : (
                <div className={`${addCurrentThemeClassToEle(currentTheme, 'examMain0')}`}>
                    <h1 className='text-[22px] font-medium mt-[30px] mb-[25px] examPlaceHolderTitle'>
                        {dashboard.evaluateSection.test.title}
                    </h1>
                    <div>
                        {testDetails.length > 0 ? (
                            <div className="examMain rounded-[7px] grid max-[1200px]:grid-cols-1 grid-cols-2 bg-[#f8f8f8] max-[1200px]:bg-white">
                                {testDetails.map((exam: any, index: number) => (
                                    <ExamCard
                                        key={index}
                                        exam={exam}
                                        examType={examType.practice}
                                        source={"dashboard"}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div
                                ref={noTestRef}
                                className='examMain text-[#949494] rounded-[10px] flex max-[1200px]:bg-[#ffffff] bg-[#F8F8F8] items-center justify-center'
                            >
                                <p>{dashboard.evaluateSection.exam.whenNoExam}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
